import { useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon, AddIcon } from '@/components/ui/Icons';
import { IconButton } from '@/components/ui/IconButton/IconButton';
import { Button } from '@/components/ui/Button/Button';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { useClickOutside } from '@/hooks/useClickOutside';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { TEMP_NOTE_PREFIX } from '@/utils/projectNotes';
import { generateUUID } from '@/utils/generateUUID';
import { ProjectNotesPopoverNoteItem } from './ProjectNotesPopoverNoteItem';
import { draftItemToProjectNote } from './projectNotesPopoverUtils';
import type { ProjectNotesPopoverLocalDraftProps } from './ProjectNotesPopover.types';
import { PROJECTS_TABLE_ICON_ACTION_WRAP_SELECTOR } from './projectNotesPopoverConstants';

export function ProjectNotesPopoverLocalDraft({
  open,
  onClose,
  anchorEl,
  localDraft,
}: ProjectNotesPopoverLocalDraftProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const { items, onItemsChange, titleName } = localDraft;

  const { shouldRender, contentClasses, requestClose, isExiting } = useOverlayAnimation(open, onClose, true);

  usePopoverPositioning({
    anchorEl,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionX: 'left',
    preferredPositionY: 'bottom',
    verticalAlign: 'center',
    offset: 8,
    margin: { top: 20, bottom: 20, left: 20, right: 20 },
    zIndex: 1000,
    priorityAxis: 'x',
    clampOnScrollFollow: true,
    onAnchorDetached: requestClose,
  });

  const anchorExcludeRef = useRef<HTMLElement | null>(null);
  anchorExcludeRef.current = anchorEl;

  useClickOutside(popoverRef, requestClose as (e: MouseEvent) => void, {
    excludeRefs: [anchorExcludeRef as React.RefObject<HTMLElement | null>],
    excludeSelector: PROJECTS_TABLE_ICON_ACTION_WRAP_SELECTOR,
    enabled: shouldRender,
    capture: false,
  });

  useEffect(() => {
    if (!shouldRender) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [shouldRender, requestClose]);

  const handleAddNote = useCallback(() => {
    onItemsChange([
      { id: `${TEMP_NOTE_PREFIX}${generateUUID()}`, content: '' },
      ...items,
    ]);
  }, [items, onItemsChange]);

  const handleNoteChange = useCallback(
    (noteId: string, value: string) => {
      onItemsChange(
        items.map((it) => (it.id === noteId ? { ...it, content: value } : it))
      );
    },
    [items, onItemsChange]
  );

  const handleDelete = useCallback(
    (noteId: string) => {
      if (!window.confirm('Удалить заметку?')) return;
      onItemsChange(items.filter((it) => it.id !== noteId));
    },
    [items, onItemsChange]
  );

  const popoverContent = (
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        left: -9999,
        top: -9999,
        pointerEvents: isExiting ? 'none' : 'auto',
      }}
    >
      <OverlayIntrinsicSize>
        <div
          className={`project-notes-popover ${contentClasses}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="project-notes-popover-title"
        >
      <div className="project-notes-popover__header">
        <h2 id="project-notes-popover-title" className="project-notes-popover__title">
          Заметки: {titleName || 'Проект'}
        </h2>
        <IconButton aria-label="Закрыть" onClick={requestClose} size="m" iconSize={20} variant="danger" className="project-notes-popover__close">
          <CloseIcon />
        </IconButton>
      </div>
      <div className="project-notes-popover__body">
        <OverlayScrollArea
          className="project-notes-popover__scroll"
          osOptions={{ overflow: { x: 'visible', y: 'scroll' } }}
        >
          <div className="project-notes-popover__scroll-pad">
            <ul className="project-notes-popover__list">
              {items.length === 0 ? (
                <li className="project-notes-popover__empty">
                  <i className="fa-solid fa-pen-clip project-notes-popover__empty-icon" aria-hidden="true" />
                  <p>Нет заметок</p>
                </li>
              ) : (
                items.map((item, index) => (
                  <ProjectNotesPopoverNoteItem
                    key={item.id}
                    note={draftItemToProjectNote(item, index)}
                    content={item.content}
                    onContentChange={handleNoteChange}
                    onDelete={handleDelete}
                    isDeleting={false}
                    deleteDisabled={false}
                    draftDateHint="До создания проекта"
                  />
                ))
              )}
            </ul>
          </div>
        </OverlayScrollArea>
        <div className="project-notes-popover__add-btn-wrap">
          <Button
            type="button"
            variant="outlined"
            onClick={handleAddNote}
            className="project-notes-popover__add-btn"
          >
            <AddIcon size={18} />
            Добавить заметку
          </Button>
        </div>
      </div>
        </div>
      </OverlayIntrinsicSize>
    </div>
  );

  return shouldRender ? createPortal(popoverContent, document.body) : null;
}
